import { FC, memo } from 'react'

import { HistoryTable } from 'bank-entity-history'
import { TableDataQa } from 'commons/types/enums'
import { formatCurrencyCent } from 'commons/utils'
import { Link, Panel, PanelFace } from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { DateTime } from 'ui/components/DateTime'
import { Hr } from 'ui/components/Hr'
import { Info, InfoSet, InfoSetDirection } from 'ui/components/Info'
import { Page, PageFace, PageSection, PageSectionFace } from 'ui/components/Page'

import { DetailsPageProps } from './DetailsPage.types'

import { Column } from './styles'

export const DetailsPage: FC<DetailsPageProps> = memo((props) => {
  const { data, onCancel, pagination } = props
  const {
    id,
    enableCancel,
    amount,
    date,
    createdAt,
    updatedAt,
    showApprovedAt,
    status,
    recipientBankAccountFormattedNumber,
    recipientBankAccountId,
    showRecipientLicenseId,
    showRecipientLicenseName,
    showSenderLicenseId,
    showSenderLicenseName,
    recipientCompanyName,
    senderBankAccountFormattedNumber,
    senderBankAccountId,
    senderCompanyName,
    notes,
    manifestNumber,
    history
  } = data

  return (
    <Page title="Internal Transfer Page" subTitle={`IT-${id}`} face={PageFace.SECONDARY}>
      <PageSection
        title="Internal Transfer Information"
        face={PageSectionFace.SECONDARY}
        actions={
          enableCancel && (
            <Button face="secondary" onClick={onCancel}>
              Cancel
            </Button>
          )
        }
      >
        <Hr />
        <InfoSet direction={InfoSetDirection.ROW} legend="Internal Transfer Details">
          <Column>
            <Info label="Internal transfer number">IT-{id}</Info>
            <Info label="Recipient">
              {showRecipientLicenseId ? (
                <Link to="/licenses/$licenseId" params={{ licenseId: showRecipientLicenseId.toString() }}>
                  {showRecipientLicenseName}
                </Link>
              ) : (
                <Link to="/bank-accounts/$bankAccountId" params={{ bankAccountId: recipientBankAccountId.toString() }}>
                  {recipientCompanyName} {recipientBankAccountFormattedNumber}
                </Link>
              )}
            </Info>
            <Info label="Amount">{formatCurrencyCent(amount)}</Info>
            <Info label="Date">{date}</Info>
            <Info label="Notes">{notes}</Info>
          </Column>
          <Column>
            <Info label="Status">{status}</Info>
            <Info label="Sender">
              {showSenderLicenseId ? (
                <Link to="/licenses/$licenseId" params={{ licenseId: showSenderLicenseId.toString() }}>
                  {showSenderLicenseName}
                </Link>
              ) : (
                <Link to="/bank-accounts/$bankAccountId" params={{ bankAccountId: senderBankAccountId.toString() }}>
                  {senderCompanyName} {senderBankAccountFormattedNumber}
                </Link>
              )}
            </Info>
            <Info label="Manifest Number">{manifestNumber}</Info>
          </Column>
        </InfoSet>
        <Hr />
        <InfoSet direction={InfoSetDirection.ROW} legend="System Information">
          <Column>
            <Info label="Created At">
              <DateTime date={createdAt} withTime />
            </Info>
            <Info label="Last Modified">
              <DateTime date={updatedAt} withTime />
            </Info>
          </Column>
          <Column>
            <Info label="Approval Date">
              <DateTime withTime date={showApprovedAt} />
            </Info>
          </Column>
        </InfoSet>
        <Hr />
      </PageSection>
      <PageSection title="Internal Transfer History" face={PageSectionFace.SECONDARY}>
        <Panel rounded shadowed collapsible={false} face={PanelFace.SECONDARY}>
          <HistoryTable dataQa={TableDataQa.BP_INTERNAL_TRANSFERS_HISTORY} items={history} pagination={pagination} />
        </Panel>
      </PageSection>
    </Page>
  )
})
